import React, { useRef, useState } from 'react'
import { Icon, Textarea, Typography } from '@equinor/eds-core-react'
import styled from 'styled-components'
import {
    StyledHelpButton,
    StyledHelpIcon,
    StyledTypography,
    SummaryErrorContainer,
    SummaryWarningContainer,
} from '@/styles/layout'
import { help_outline, error_outlined } from '@equinor/eds-icons'
import { DeviceDto, PlantDto, ProjectDto, ExistingImageDto } from '@/core/models/types'
import { missingCoordinates, missingSuperiorTag, missingVendorSerialNumber, missingArea } from './OnboardingStepper'
import { showErrorOverviewAtom, selectedAreaAtom } from '@/core/store/globalStore'
import { useAtom } from 'jotai'
import PopoverDialog from '@/components/popover/PopoverDialog'
interface OverviewProps {
    selectedPlant: PlantDto | null
    selectedDevice: DeviceDto | null
    selectedProject: ProjectDto | null
    currentSensorData: {
        SuperiorTag: string
        Area: string
        Coordinates: string
        Tag: string
        VendorSerialNumber: string
        ImageFiles: File[]
        ImageDescriptions: string[]
        UserAddedDescription: string
        CoordinatesNotAvailable: boolean
        ExistingImages: ExistingImageDto[]
    } | null
    sensorData: any[]
    onUpdate: (data: any) => void
    displayAdditionalDescription: boolean
    displaySensorData: boolean
    displayImageNumber: boolean
}

const Overview: React.FC<OverviewProps> = ({
    selectedPlant,
    selectedDevice,
    selectedProject,
    currentSensorData,
    sensorData,
    onUpdate,
    displayAdditionalDescription,
    displaySensorData,
    displayImageNumber,
}) => {
    const [isTooltipDescriptionOpen, setTooltipDescriptionIsOpen] = useState(false)

    const tooltipDescription = 'Add extra information for the sensor.'
    const [showErrorOverview] = useAtom(showErrorOverviewAtom)
    const [selectedArea] = useAtom(selectedAreaAtom)

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { id, value } = e.target
        onUpdate({ ...currentSensorData, [id]: value })
    }

    const descriptionRef = useRef<HTMLDivElement>(null)

    return (
        <ConfirmationContainer>
            <OverView>
                <RowStyle>
                    <StyledTypographyHeader>OVERVIEW</StyledTypographyHeader>
                </RowStyle>
                <RowStyle>
                    <StyledTypography>Plant: {selectedPlant?.description || ''}</StyledTypography>
                </RowStyle>
                <RowStyle>
                    <StyledTypography>
                        Project: {`${selectedProject?.projectCode} - ${selectedProject?.description}` || ''}
                    </StyledTypography>
                </RowStyle>
                <RowStyle>
                    <StyledTypography>
                        Vendor serial number: {currentSensorData?.VendorSerialNumber || ''}
                    </StyledTypography>
                </RowStyle>
                {showErrorOverview && !currentSensorData?.VendorSerialNumber && (
                    <SummaryErrorOverviewContainer>
                        <Icon data={error_outlined} title="Error" size={16} />
                        <StyledErrorTypography>{missingVendorSerialNumber}</StyledErrorTypography>
                    </SummaryErrorOverviewContainer>
                )}
                <RowStyle>
                    <StyledTypography>Device: {selectedDevice?.key || ''}</StyledTypography>
                </RowStyle>
                {displaySensorData && (
                    <>
                        <RowStyle>
                            <StyledTypography>Superior tag: {currentSensorData?.SuperiorTag || ''}</StyledTypography>
                        </RowStyle>
                        {showErrorOverview && !currentSensorData?.SuperiorTag && (
                            <SummaryWarningOverviewContainer>
                                <Icon data={error_outlined} title="Warning" size={16} />
                                <StyledWarningTypography>{missingSuperiorTag}</StyledWarningTypography>
                            </SummaryWarningOverviewContainer>
                        )}
                        <RowStyle>
                            <StyledTypography>
                                Area: {selectedArea ? `${selectedArea.areaCode} - ${selectedArea.description}` : ''}
                            </StyledTypography>
                        </RowStyle>
                        {showErrorOverview && !currentSensorData?.Area && (
                            <SummaryWarningOverviewContainer>
                                <Icon data={error_outlined} title="Warning" size={16} />
                                <StyledWarningTypography>{missingArea}</StyledWarningTypography>
                            </SummaryWarningOverviewContainer>
                        )}
                        <RowStyle>
                            <StyledTypography>Coordinates: {currentSensorData?.Coordinates || ''}</StyledTypography>
                        </RowStyle>
                        {showErrorOverview &&
                            !currentSensorData?.CoordinatesNotAvailable &&
                            !currentSensorData?.Coordinates && (
                                <SummaryWarningOverviewContainer>
                                    <Icon data={error_outlined} title="Warning" size={16} />
                                    <StyledWarningTypography>{missingCoordinates}</StyledWarningTypography>
                                </SummaryWarningOverviewContainer>
                            )}
                        <RowStyle>
                            {displayImageNumber && (
                                <StyledTypography>
                                    Images:{' '}
                                    {String(
                                        (currentSensorData?.ImageFiles?.length ?? 0) +
                                            (currentSensorData?.ExistingImages?.length ?? 0)
                                    )}
                                </StyledTypography>
                            )}
                        </RowStyle>
                    </>
                )}
            </OverView>
            {displayAdditionalDescription && (
                <>
                    <ConfirmationContainer style={{ marginTop: '16px' }}>
                        <RowStyle>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <OverviewInputLabel>Additional description</OverviewInputLabel>
                                <StyledHelpButton
                                    variant="ghost_icon"
                                    ref={descriptionRef}
                                    onClick={() => setTooltipDescriptionIsOpen(!isTooltipDescriptionOpen)}
                                >
                                    <StyledHelpIcon data={help_outline} size={24} />
                                </StyledHelpButton>
                            </div>
                            <div style={{ marginLeft: 'auto' }}>
                                <OverviewInputLabel>Optional</OverviewInputLabel>
                            </div>
                        </RowStyle>
                    </ConfirmationContainer>
                    <StyledTextArea
                        id="UserAddedDescription"
                        value={currentSensorData?.UserAddedDescription}
                        onChange={handleChange}
                        required
                        rows={3}
                        maxLength={150}
                        placeholder={'Add description'}
                    />
                    <PopoverDialog
                        open={isTooltipDescriptionOpen}
                        title={'Additional description'}
                        content={tooltipDescription}
                        onClose={() => setTooltipDescriptionIsOpen(false)}
                        anchorEl={descriptionRef.current}
                    />
                </>
            )}
        </ConfirmationContainer>
    )
}

const ConfirmationContainer = styled.div`
    width: 100%;
`

const OverView = styled.div`
    display: flex;
    flex-direction: column; /* Stack overview items vertically */
    padding: 8px;
    margin: 0;
    background: #f7f7f7;
    color: #3d3d3d;
    border-radius: 4px;
    @media (max-width: 1024px) {
        background: #f5f5f5;
        color: #1d1d1d;

        ${StyledTypography} {
            color: #1d1d1d;
        }
    }
`

const StyledTextArea = styled(Textarea)`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    color: white;
    margin-bottom: 0px;

    @media (max-width: 1024px) {
        color: #1d1d1d;
        textarea {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
    }
`

const OverviewInputLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    color: #6f6f6f;
    @media (max-width: 1024px) {
        color: #585858;
    }
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    > * {
        margin-right: 0.3rem;
    }
`

const StyledWarningTypography = styled(Typography)`
    gap: 8px;
    text-decoration: none;
    background: none;
    border: none;
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    line-height: 16px;
    font-weight: 500;
    flex-shrink: 0;
    min-width: 0;
    flex: 1;
    color: #ad6200;
    @media (max-width: 1024px) {
        color: #8a4100;
    }
`

const StyledErrorTypography = styled(Typography)`
    ${StyledWarningTypography}
    color: #b30d2f;
    @media (max-width: 1024px) {
        color: #a50827;
    }
`

const StyledTypographyHeader = styled(Typography)`
    gap: 8px;
    text-decoration: none;
    background: none;
    border: none;
    font-family: Equinor;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 1.1px;
    color: #3d3d3d;
    @media (max-width: 1024px) {
        color: #1d1d1d;
    }
`

const SummaryErrorOverviewContainer = styled(SummaryErrorContainer)`
    margin-left: 0px;
`

const SummaryWarningOverviewContainer = styled(SummaryWarningContainer)`
    margin-left: 0px;
    align-items: start;
    @media (max-width: 1024px) {
        color: #8a4100;
    }
`

export default Overview
